import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { teceoGrayPrimary } from '../../../../../../../../Theme';
import { BUTTON_TO_OPEN_CHAT_ID } from '../../../../../../../../components/AppWrap/components/OpenChatButton/OpenChatButton';
import useWidth from '../../../../../../../../components/Hooks/useWidth';
import { useFeatureFlag } from '../../../../../../../../hooks/useFeatureFlag';
import CenteredMinimize from '../../../../../../../../icons/CenteredMinimize';
import LangEnum from '../../../../../../../../lang/LangEnum';
import useTeceoStyles from '../../../../../../../../useTeceoStyles';
import { getColorByRole } from '../../../../../../../../utils/generics';
import { useApplicationContext } from '../../../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { useChatContext } from '../../../../../../contexts/chat/chat.context';
import { useChatModalContext } from '../../../../../../contexts/chatModal/ChatModal.context';
import { ChatUser } from '../../../../../../types/chat.types';
import chatRepository from '../../../../../../utils/chat.repository';
import { invertChatRole, isGroupChat, isPrivateChat } from '../../../../../../utils/chat.utils';
import ChatAvatar from '../../../../../ChatAvatar/ChatAvatar';
import { formatBrandStoreChat } from '../../../../../ChatConfigViews/components/ChatContainerHeader/chatHeader.utils';
import ChatHeaderTemplate from '../../../../../ChatHeaderTemplate/ChatHeaderTemplate';
import { useChatMessagesContext } from '../../../../contexts/chatMessages.context';
import { useChatMessagesHeaderStyles } from './chatMessagesHeader.styles';
import ChatMessagesMenu from './components/ChatMessagesMenu';

const formatUsersParticipants = (users: ChatUser[]) =>
  users.map((user, index) => `${index > 0 ? ', ' : ''}${user.name || user.email}`);

const ChatMessagesHeader = () => {
  const { mobileWidth } = useWidth();
  const { classes: teceoClasses } = useTeceoStyles();
  const { translate } = useTranslation();
  const { isEnableChatVideoCall } = useFeatureFlag();

  const { classes } = useChatMessagesHeaderStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoadingUsersParticipants, setIsLoadingUsersParticipants] = useState(false);
  const [usersParticipants, setUsersParticipants] = useState<ChatUser[]>([]);

  const { open } = useChatModalContext();
  const { activeChat, hasErrorInConnectionToWs, videoCall, closeActiveChat, minimizeActiveChat } =
    useChatContext();

  const { goToEditChat, initVideoCall, isSendingFirstMessageInChatWithoutId } =
    useChatMessagesContext();

  const { isAdminBrand, isStore, userRole } = useApplicationContext();

  const handleClickOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleGoBackMobile = useCallback(() => {
    const openChatButtonEl = document.querySelector<HTMLButtonElement>(
      `#${BUTTON_TO_OPEN_CHAT_ID}`,
    );
    if (openChatButtonEl) {
      open(openChatButtonEl);
    }

    setTimeout(() => {
      closeActiveChat();
    }, 150);
  }, [closeActiveChat, open]);

  const { title, logoUrl } = React.useMemo(() => {
    if (!activeChat) {
      return { title: '', logoUrl: '' };
    }

    return formatBrandStoreChat({ chat: activeChat, isStore, translate });
  }, [activeChat, isStore]);

  const showMoreMenu = isAdminBrand && activeChat && isGroupChat(activeChat.type);
  const showMinimizeButton = !!activeChat?.id;

  const videoCallButtonDisabled =
    isSendingFirstMessageInChatWithoutId ||
    hasErrorInConnectionToWs ||
    (videoCall && activeChat && activeChat.inProgressVideoCall?.meetingId === videoCall?.meetingId);

  const getUsersParticipants = useCallback(async () => {
    if (!activeChat?.id) {
      setUsersParticipants([]);
      return;
    }

    setIsLoadingUsersParticipants(true);
    const response = await chatRepository.getChatUsersParticipants(activeChat.id);
    setIsLoadingUsersParticipants(false);
    if (response.ok && response.data) {
      setUsersParticipants(response.data.data);
    }
  }, [activeChat?.id]);

  const getMessage = () => {
    if (isLoadingUsersParticipants) {
      return translate(LangEnum.CHAT_MESSAGES_HEADER_LOADING_PARTICIPANTS);
    }
    if (usersParticipants.length === 0) {
      return translate(LangEnum.CHAT_MESSAGES_HEADER_NO_PARTICIPANTS);
    }
    return formatUsersParticipants(usersParticipants);
  };

  useEffect(() => {
    getUsersParticipants();
  }, [getUsersParticipants]);

  return (
    <>
      <ChatMessagesMenu onClickEdit={goToEditChat} anchorEl={anchorEl} onClose={handleCloseMenu} />
      <ChatHeaderTemplate.Template
        classes={{
          wrapper: classes.chatHeaderTemplateWrapper,
        }}
        onClickGoBack={mobileWidth ? handleGoBackMobile : undefined}
        onClose={closeActiveChat}
        action={
          <Grid container spacing={1}>
            {isEnableChatVideoCall && (
              <Grid>
                <IconButton
                  disabled={videoCallButtonDisabled}
                  onClick={initVideoCall}
                  size="small"
                  style={{ color: teceoGrayPrimary }}
                >
                  <VideocamRoundedIcon />
                </IconButton>
              </Grid>
            )}

            {showMoreMenu ? (
              <Grid>
                <IconButton
                  onClick={handleClickOpenMenu}
                  size="small"
                  style={{ color: teceoGrayPrimary }}
                >
                  <MoreVertRoundedIcon fontSize="medium" />
                </IconButton>
              </Grid>
            ) : null}

            {showMinimizeButton && (
              <Grid className={classes.minimizeButtonWrapper}>
                <IconButton
                  onClick={minimizeActiveChat}
                  size="small"
                  style={{ color: teceoGrayPrimary }}
                >
                  <CenteredMinimize fontSize="medium" />
                </IconButton>
              </Grid>
            )}
          </Grid>
        }
      >
        <Grid>
          <Box
            sx={{
              paddingRight: 1,
            }}
          >
            <ChatAvatar
              size={4.5}
              src={logoUrl}
              title={title}
              chatType={activeChat?.type}
              backgroundColor={getColorByRole({
                withSrc: !!logoUrl,
                transparentWhenHasSrc: false,
                userRole: invertChatRole(
                  !!activeChat && isPrivateChat(activeChat.type),
                  isStore,
                  userRole,
                ),
              })}
            />
          </Box>
        </Grid>
        <Grid className={classes.textWrapper}>
          <Typography color="textPrimary" className={teceoClasses.truncate}>
            {title}
          </Typography>

          <Typography className={teceoClasses.truncate} variant="body2" color="textSecondary">
            {getMessage()}
          </Typography>
        </Grid>
      </ChatHeaderTemplate.Template>
    </>
  );
};

export default React.memo(ChatMessagesHeader);
